import {
  BookOpenIcon,
  ChatAltIcon,
  InformationCircleIcon,
  LogoutIcon,
} from "@heroicons/react/solid";
import { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ApiCommonParams } from "../../api/@types/apiTypes";
import { ApiResponse } from "../../api/apiResponse";
import { indexSkills } from "../../api/endpoints/candidate/skills";
import NoteModel from "../../api/models/NoteModel";
import SessionModel from "../../api/models/SessionModel";
import { useAuth, UserType } from "../../context/AuthContext";
import { showApiErrorMessages } from "../../helpers";
import LoadingSpinner from "../LoadingSpinner";
import QuranViewer from "../quran-viewer";
import SessionNotes from "./NotesAndSkills/SessionsNotes";
import SessionInfo from "./SessionInfo";
import UserSessionRoom from "./UserSessionRoom";
import ExistConfirmationModal from "../modal/ExistSessionModal";

type Props = {
  getUserSession: (
    sessionId: string | number,
    referrer: string
  ) => Promise<ApiResponse>;
  indexNotes: (
    commonApiParams?: ApiCommonParams | undefined
  ) => Promise<ApiResponse>;
};

// This is wrapper for the reading room for both students and candidates
export default function StudentReadingRoom(props: Props) {
  const { getUserSession, indexNotes } = props;

  const navigate = useNavigate();
  const [showTrailingContent, setShowTrailingContent] = useState(false);
  const [trailingContentIndex, setTrailingContentIndex] = useState(0);
  const [session, setSession] = useState<null | SessionModel>(null);
  const [noteState, setNoteState] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<undefined | number>(1);

  const authContext = useAuth();
  const { id } = useParams();
  const jitsiApiRef = useRef<IJitsiMeetExternalApi>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const getSession = async () => {
    const userSessionApi = await getUserSession(id!, "meeting_room");
    if (userSessionApi.isSuccess) {
      const userSession: SessionModel = SessionModel.fromApiResponse(
        userSessionApi.data
      );
      setSession(userSession);
      setPageNumber(userSession!.lastQuranPageReached! ?? 1);
    }
  };
  const listAndSetStudentNotes = (
    orderBy: any,
    setNotes: any,
    noteState: any
  ) => {
    setNoteState(noteState);
  };

  const { data: sortedNotes } = useQuery(
    ["studentNotes", id, noteState],
    () => {
      let apiParams: { [key: string]: any } = {
        session_id: id,
      };
      if (!!noteState) apiParams["state"] = noteState;
      return indexNotes({ filters: apiParams, orderBy: "-id" });
    },
    {
      refetchInterval: 1 * 60 * 1000, // 1 minutes
      select: (response: ApiResponse) => {
        if (!response.isSuccess) {
          showApiErrorMessages(response);
          return [];
        }
        return NoteModel.fromApiResponse(response.data);
      },
    }
  );

  const { data: finishedSkills } = useQuery(["candidateSkills"], indexSkills, {
    refetchInterval: 1 * 60 * 1000, // 1 minutes
    enabled: authContext?.userType === UserType.Candidate,
    select: (response) => {
      let skills: string[] | undefined = undefined;
      if (response.isSuccess) {
        skills = response.data?.skills;
      }
      return skills;
    },
  });

  useEffect(() => {
    getSession();
  }, []);
  const viewsSize = [
    { sm: 40, md: 55, max: "360px" },
    { sm: 40, md: 50, max: "640px" },
    { sm: 40, md: 50, max: "640px" },
    { sm: 40, md: 55, max: "640px" },
  ];

  const views = [
    <div className="sm:h-screen h-full">
      {!!session ? (
        <SessionInfo
          session={session}
          onClose={() => {
            setShowTrailingContent(false);
          }}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
    <div className="sm:h-screen h-full ">
      {!!session ? (
        <QuranViewer
          onClose={(val: number | undefined) => {
            setShowTrailingContent(false);
            setTimeout(() => {
              setTrailingContentIndex(0);
            }, 10);
            setPageNumber(val);
          }}
          startPage={!!pageNumber ? pageNumber : 1}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
    <div className=" overflow-y-hidden h-full ">
      {!!session ? (
        <SessionNotes
          listAndSetStudentNotes={listAndSetStudentNotes}
          isEditable={false}
          sessionId={session.id}
          notes={sortedNotes}
          onClose={() => {
            setShowTrailingContent(false);
          }}
          finishedSkills={finishedSkills}
          showSkills={authContext?.userType === UserType.Candidate}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
  ];
  const onCloseModal = (val: boolean) => {
    if (val) {
      navigate(
        `/${authContext?.userType}/dashboard/room/?sessionId=${session?.id}&sessionNumber=${session?.sessionNumber}&studentName=${session?.tutor?.name}`
      );
    } else {
      setIsOpen(false);
    }
  };
  const navigationItems = useMemo(() => {
    const { currentUser }: any = authContext;
    if (currentUser?.programName !== "ejaza") {
      return [
        {
          Icon: InformationCircleIcon,
          action: () => showContent(0),
          color: "white",
          iconColor: "gray-600",
        },
        {
          Icon: BookOpenIcon,
          action: () => showContent(1),
          color: "white",
          iconColor: "gray-600",
        },
        {
          Icon: ChatAltIcon,
          action: () => showContent(2),
          color: "white",
          iconColor: "gray-600",
        },

        {
          Icon: LogoutIcon,
          action: () => {
            setIsOpen(true);
          },
          color: "red-600 rtl:rotate-180",
          iconColor: "white",
        },
      ];
    } else {
      return [
        {
          Icon: InformationCircleIcon,
          action: () => showContent(0),
          color: "white",
          iconColor: "gray-600",
        },

        {
          Icon: ChatAltIcon,
          action: () => showContent(2),
          color: "white",
          iconColor: "gray-600",
        },

        {
          Icon: LogoutIcon,
          action: () => {
            setIsOpen(true);
          },
          color: "red-600 rtl:rotate-180",
          iconColor: "white",
        },
      ];
    }
  }, [authContext]);

  const showContent = (index: number) => {
    setShowTrailingContent(false);
    setTrailingContentIndex(index);
    setShowTrailingContent(true);
  };

  return (
    <>
      {!!session && (
        <>
          <UserSessionRoom
            showTrailingContent={showTrailingContent}
            session={session}
            navigationItems={navigationItems}
            viewsSize={viewsSize}
            jitsiApiRef={jitsiApiRef}
            views={views}
            userType={authContext?.userType}
            trailingContentIndex={trailingContentIndex}
            trailingMaxWidth={
              viewsSize[trailingContentIndex].max as "360px" | "640px"
            }
          />
          <ExistConfirmationModal isOpen={isOpen} onClose={onCloseModal} />
        </>
      )}
    </>
  );
}
